// src/components/CustomizationPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { getTemplate } from '../utils/supabaseUtils';
import { exportTemplate } from '../utils/cesdkUtils';

const CustomizationPage = ({ cesdk }) => {
  const { templateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const uploadedImageURL = location.state?.uploadedImageURL;

  const [user, setUser] = useState(null);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [formData, setFormData] = useState({});
  const [placeholders, setPlaceholders] = useState([]);
  const [exportURL, setExportURL] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Authentication and initial data fetch
  useEffect(() => {
    const fetchSessionAndData = async () => {
      setLoading(true);
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        navigate('/login');
        return;
      }
      setUser(data.session.user);

      // Fetch primary image (similar to UserDashboard)
      const { data: images } = await supabase.storage
        .from('playmakerpics-images')
        .list(`background_removed_images/${data.session.user.id}`, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'name', order: 'asc' },
        });
      if (images?.length > 0) {
        const primaryUrl = supabase.storage
          .from('playmakerpics-images')
          .getPublicUrl(`background_removed_images/${data.session.user.id}/${images[0].name}`)
          .data.publicUrl;
        setPrimaryImage(primaryUrl);
      }

      // Fetch template data
      try {
        const templateData = await getTemplate(templateId);
        if (!templateData) throw new Error('Template not found');
        setPlaceholders(templateData.placeholder_data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSessionAndData();
  }, [templateId, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const replacementData = { ...formData };

      // Replace 'source' placeholders with primary image
      const sourcePlaceholder = placeholders.find(p => p.name.toLowerCase() === 'source');
      if (sourcePlaceholder && primaryImage) {
        replacementData[sourcePlaceholder.name] = primaryImage;
      } else if (sourcePlaceholder && uploadedImageURL) {
        // Fallback to uploaded image if no primary image
        replacementData[sourcePlaceholder.name] = uploadedImageURL;
      }

      const url = await exportTemplate(cesdk, templateId, replacementData);
      setExportURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="customization-page">
      <h2>Customize Template</h2>
      {loading && <p>Loading...</p>}
      {error && <p className="error">{error}</p>}
      
      <form onSubmit={handleSubmit}>
        {placeholders.map(placeholder => {
          if (placeholder.type === '//ly.img.ubq/text') {
            return (
              <div key={placeholder.name} className="form-group">
                <label>{placeholder.name}</label>
                <input
                  type="text"
                  name={placeholder.name}
                  value={formData[placeholder.name] || ''}
                  onChange={handleInputChange}
                  placeholder={`Enter ${placeholder.name}`}
                  disabled={loading}
                />
              </div>
            );
          }
          if (placeholder.name.toLowerCase() === 'source' && (primaryImage || uploadedImageURL)) {
            return (
              <div key={placeholder.name} className="form-group">
                <label>{placeholder.name}</label>
                <p>Using {primaryImage ? 'Primary Image' : 'Uploaded Image'}</p>
                <img 
                  src={primaryImage || uploadedImageURL} 
                  alt="Source preview" 
                  style={{ maxWidth: '200px' }}
                />
              </div>
            );
          }
          return null;
        })}
        <button type="submit" disabled={loading}>
          {loading ? 'Exporting...' : 'Export Template'}
        </button>
      </form>

      {exportURL && (
        <div className="export-result">
          <h3>Your Customized Design</h3>
          <img src={exportURL} alt="Exported design" />
          <a href={exportURL} download="customized-design.png">
            Download Exported Image
          </a>
        </div>
      )}
    </div>
  );
};

export default CustomizationPage;